import React from 'react'

import {
  CompensationsBasicPayrollRunInfoDocument,
  PayrollRunInfoPayrollRunFragment,
} from 'generated/resources/graphql'
import moment from 'moment'

import { useUnsafeEffect } from 'modules/core/hooks/useEffect'
import { useQuery } from 'modules/core/hooks/useQuery'

interface PayrollRunsContextType {
  loading: boolean
  currentPayrollRun?: PayrollRunInfoPayrollRunFragment
  payrollRuns: PayrollRunInfoPayrollRunFragment[]
  setCurrentPayrollRun: (id: string) => void
}

const initialState: PayrollRunsContextType = {
  loading: true,
  currentPayrollRun: undefined,
  payrollRuns: [],
  setCurrentPayrollRun: () => {},
}

const PayrollRunsContext = React.createContext<PayrollRunsContextType>(initialState)

export const PayrollRunsProvider = ({ children }: { children: React.ReactNode }) => {
  const { data, loading } = useQuery(CompensationsBasicPayrollRunInfoDocument, {})

  const payrollRuns = React.useMemo(
    () => data?.compensations?.payrollRunsConnection?.nodes || [],
    [data?.compensations?.payrollRunsConnection?.nodes]
  )

  const [currentPayrollRun, _setCurrentPayrollRun] = React.useState<
    PayrollRunInfoPayrollRunFragment | undefined
  >(undefined)

  useUnsafeEffect(() => {
    if (currentPayrollRun || payrollRuns.length === 0) {
      return
    }

    const now = moment()
    const _currentPayrollRun = payrollRuns.find((element) => {
      const startsOn = moment(element.startsOn)
      const endsOn = moment(element.endsOn)
      return now.isBetween(startsOn, endsOn, 'day', '[]')
    })

    if (_currentPayrollRun) {
      _setCurrentPayrollRun(_currentPayrollRun)
    } else {
      _setCurrentPayrollRun(payrollRuns[0])
    }
  }, [payrollRuns, currentPayrollRun])

  const setCurrentPayrollRun = (id: string) => {
    const payrollRun = payrollRuns.find((element) => element.id === id)
    if (payrollRun) {
      _setCurrentPayrollRun(payrollRun)
    }
  }

  const value = {
    loading,
    currentPayrollRun,
    payrollRuns,
    setCurrentPayrollRun,
  }

  return <PayrollRunsContext.Provider value={value}>{children}</PayrollRunsContext.Provider>
}

export const usePayrollRuns = () => {
  const context = React.useContext(PayrollRunsContext)

  if (!context) {
    throw new Error('usePayrollRuns must be used within a PayrollRunsProvider')
  }

  return context
}
